import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Calendar as CalendarIcon, CheckCircle, TrendingUp } from 'lucide-react';

const Calendar = () => {
  const { t } = useLanguage();

  const yearColors = [
    'from-blue-500 to-cyan-500',
    'from-cyan-500 to-teal-500',
    'from-teal-500 to-green-500',
    'from-orange-500 to-red-500',
    'from-purple-500 to-pink-500'
  ];

  return (
    <section id="calendar" className="relative py-24 bg-gray-950">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 mb-6">
            <CalendarIcon className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-medium text-blue-400">Roadmap 2026-2030</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            {t.calendar.title}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t.calendar.subtitle}
          </p>
        </div>

        {/* Background Image Grid */}
        <div className="grid grid-cols-3 gap-4 mb-12 opacity-20">
          <img src="https://images.unsplash.com/photo-1618536516485-006eb4d1e9bb?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Team" className="w-full h-32 object-cover rounded-lg" />
          <img src="https://images.unsplash.com/photo-1706102005359-3d6f2a6ec313?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Training" className="w-full h-32 object-cover rounded-lg" />
          <img src="https://images.unsplash.com/photo-1725622428670-40a7e9add82f?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Unity" className="w-full h-32 object-cover rounded-lg" />
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical Line */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 via-teal-500 to-purple-500 transform -translate-x-1/2" />

          {/* Years */}
          <div className="space-y-12">
            {t.calendar.years.map((year, index) => {
              const isEven = index % 2 === 0;
              const colorScheme = yearColors[index];

              return (
                <div
                  key={index}
                  className={`relative flex flex-col lg:flex-row items-center gap-8 ${
                    isEven ? '' : 'lg:flex-row-reverse'
                  }`}
                >
                  {/* Content Card */}
                  <div className={`w-full lg:w-5/12 ${isEven ? 'lg:text-right' : 'lg:text-left'}`}>
                    <div className="group relative bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 sm:p-8 hover:border-blue-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/20">
                      {/* Year Badge */}
                      <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-gradient-to-r ${colorScheme} mb-4`}>
                        <TrendingUp className="w-4 h-4 text-white" />
                        <span className="text-lg font-bold text-white">{year.year}</span>
                      </div>

                      {/* Title */}
                      <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                        {year.title}
                      </h3>

                      {/* Focus */}
                      <p className="text-blue-400 font-semibold mb-6">
                        {year.focus}
                      </p>

                      {/* Objectives */}
                      <ul className="space-y-3">
                        {year.objectives.map((objective, oIndex) => (
                          <li
                            key={oIndex}
                            className={`flex items-start space-x-3 ${
                              isEven ? 'lg:flex-row-reverse lg:space-x-reverse' : ''
                            }`}
                          >
                            <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-300">{objective}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Center Circle */}
                  <div className="hidden lg:flex items-center justify-center w-2/12">
                    <div className={`w-16 h-16 bg-gradient-to-br ${colorScheme} rounded-full flex items-center justify-center shadow-lg border-4 border-gray-950 z-10`}>
                      <span className="text-white font-bold text-xl">{String(index + 1).padStart(2, '0')}</span>
                    </div>
                  </div>

                  {/* Spacer */}
                  <div className="hidden lg:block w-5/12" />
                </div>
              );
            })}
          </div>
        </div>

        {/* KPIs Section */}
        <div className="mt-24">
          <h3 className="text-3xl font-bold text-white text-center mb-12">
            {t.kpis.title}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {t.kpis.list.map((kpi, index) => (
              <div
                key={index}
                className="group relative bg-gradient-to-br from-gray-900/80 to-gray-900/40 backdrop-blur-sm border border-gray-800 rounded-xl p-6 text-center hover:border-blue-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/20 hover:-translate-y-1"
              >
                <div className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-red-400 bg-clip-text text-transparent mb-2">
                  {kpi.value}
                </div>
                <div className="text-sm text-gray-400 leading-tight">
                  {kpi.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Calendar;
